import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { toast } from "react-toastify";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import TurningReceiveForm from "../../components/turningReceives/TurningReceiveForm";
import { LoadingState } from "../../components/ui/TableStateRow";
import cx from "../../components/ui/cx";
import { cardClass, pageTitleClass } from "../../components/ui/styles";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  clearCurrentTurningReceive,
  clearTurningReceiveOpenOrders,
  fetchTurningReceiveDetail,
  fetchTurningReceiveOpenOrders,
  fetchTurningReceiveParties,
  getCurrentTurningReceive,
  getCurrentTurningReceiveLoading,
  getTurningReceiveOpenOrders,
  getTurningReceiveOpenOrdersLoading,
  getTurningReceivePartyOptions,
} from "../../features/turningReceives/turningReceivesSlice";
import { getLoggingUser } from "../../features/loggingUser/loggingUserSlice";
import { PUT } from "../../services/axios.service";
import { receiveLinePayload } from "../../static/cuttingLine";
import { hasPermission, permission } from "../../static/permission";
import { rateText } from "../../static/processRate";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import { ITurningReceiveFormInput } from "../../types/turningReceive";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "ટર્નિંગ રિસીવ", href: route.turningReceives },
  { label: "રિસીવ સુધારો" },
];

const TurningReceiveEditPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const currentReceive = useAppSelector(getCurrentTurningReceive);
  const currentReceiveLoading = useAppSelector(getCurrentTurningReceiveLoading);
  const partyOptions = useAppSelector(getTurningReceivePartyOptions);
  const openOrders = useAppSelector(getTurningReceiveOpenOrders);
  const openOrdersLoading = useAppSelector(getTurningReceiveOpenOrdersLoading);
  const loggingUser = useAppSelector(getLoggingUser);

  const withRates = hasPermission(
    loggingUser?.permissions,
    permission.turningRate
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const receiveId = currentReceive?.id;
  const receivePartyId = currentReceive?.party_id;

  useEffect(() => {
    if (id) {
      dispatch(fetchTurningReceiveDetail(Number(id)));
    }
    return () => {
      dispatch(clearCurrentTurningReceive());
      dispatch(clearTurningReceiveOpenOrders());
    };
  }, [id, dispatch]);

  // The receive's own party stays in the list, and its own orders stay listed with its own pieces left out.
  useEffect(() => {
    if (receiveId && receivePartyId) {
      dispatch(fetchTurningReceiveParties(receivePartyId));
      dispatch(
        fetchTurningReceiveOpenOrders({
          party_id: receivePartyId,
          receive_id: receiveId,
        })
      );
    }
  }, [receiveId, receivePartyId, dispatch]);

  const handlePartyChange = useCallback(
    (partyId: number) => {
      if (partyId) {
        dispatch(
          fetchTurningReceiveOpenOrders({
            party_id: partyId,
            receive_id: Number(id),
          })
        );
      } else {
        dispatch(clearTurningReceiveOpenOrders());
      }
    },
    [id, dispatch]
  );

  const handleSubmit = async (data: ITurningReceiveFormInput) => {
    setIsSubmitting(true);
    try {
      await PUT(`/v1/admin/turning-receives/${id}`, {
        party_id: data.party_id,
        date: moment(data.date).format("YYYY-MM-DD HH:mm:ss"),
        items: receiveLinePayload(data.items, withRates),
      })
        .then((response: any) => {
          if (response.data.status === "success") {
            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
            navigate(route.turningReceives);
          } else {
            toast.error(response.data.message, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error.data.message, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error("કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.", {
        ...(toastStyles as any),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultValues = useMemo((): ITurningReceiveFormInput | undefined => {
    if (!currentReceive) return undefined;
    return {
      party_id: currentReceive.party_id,
      date: moment(currentReceive.date).format("YYYY-MM-DDTHH:mm"),
      items: (currentReceive.items || []).map((item) => ({
        order_item_id: item.order_item_id,
        qty: String(item.qty),
        rate: rateText(item.rate),
        close_order: !!item.close_order,
      })),
    };
  }, [currentReceive]);

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex items-center mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>ટર્નિંગ રિસીવ સુધારો</h1>
      </div>

      <div className="flex flex-col gap-6">
        {currentReceiveLoading ? (
          <div className={cx(cardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : currentReceive && defaultValues ? (
          <TurningReceiveForm
            key={currentReceive.id}
            defaultValues={defaultValues}
            savedReceive={currentReceive}
            partyOptions={partyOptions}
            openOrders={openOrders}
            openOrdersLoading={openOrdersLoading}
            onPartyChange={handlePartyChange}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onCancel={() => navigate(route.turningReceives)}
            submitLabel="સાચવો"
            withRates={withRates}
          />
        ) : (
          <div className={cx(cardClass, "p-8 text-center text-gray-500")}>
            ટર્નિંગ રિસીવ ની વિગતો મળી નથી
          </div>
        )}
      </div>
    </AppLayout>
  );
};
export default TurningReceiveEditPage;
